import { useMemo, useRef, useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { VariableSizeList as List } from 'react-window';
import {
  GroupBase,
  MenuListProps,
  chakraComponents,
} from 'chakra-react-select';
import { useAppDispatch } from 'app/store';
import { setLoadMore } from 'app/store/Profile/slice';
import { OPTION_HEIGHT_PIXELS, GROUP_HEADER_HEIGHT_PIXELS } from '../constants';

const MenuListVirtualized = <Option extends {}>({
  children,
  ...props
}: MenuListProps<Option, false, GroupBase<Option>>) => {
  const dispatch = useAppDispatch();
  const listRef = useRef<List>(null);
  const ignoreScrollToFocus = useRef(false);
  const isNoOptions = !Array.isArray(children);
  const { maxHeight } = props.getStyles('menuList', {
    children,
    ...props,
  }) as { maxHeight: number };

  const isWithGroups =
    props.options.length > 0 &&
    Boolean((props.options[0] as GroupBase<Option>)?.options);

  const flatChildren = useMemo(() => {
    if (isWithGroups) {
      return (!isNoOptions ? children : [])
        .map((child) => [
          {
            type: 'header',
            component: child.props.label,
          },
          ...child.props.children,
        ])
        .reduce((acc, val) => acc.concat(val), []);
    }
    return !isNoOptions ? children : [];
  }, [children, isNoOptions, isWithGroups]);

  const totalAvailableOptions = useMemo(
    () =>
      isNoOptions
        ? 1
        : children.reduce(
            (acc, val) =>
              isWithGroups ? val.props.children.length + acc : acc + 1,
            0
          ),
    [children, isNoOptions, isWithGroups]
  );

  const optionsHeightPx = totalAvailableOptions * OPTION_HEIGHT_PIXELS;

  const groupHeadersHeightPx =
    isWithGroups && !isNoOptions
      ? children.length * GROUP_HEADER_HEIGHT_PIXELS
      : 0;

  const listHeightPx = optionsHeightPx + groupHeadersHeightPx;

  useEffect(() => {
    if (isWithGroups && listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [isWithGroups, totalAvailableOptions]);

  useEffect(() => {
    if (listRef.current && !ignoreScrollToFocus.current) {
      const { getOptionValue } = props.selectProps;

      const focusedIndex = flatChildren.findIndex((child) => {
        if (!child.props?.data || !props.focusedOption) {
          return false;
        }
        return (
          getOptionValue(child.props?.data) ===
          getOptionValue(props.focusedOption)
        );
      });
      listRef.current.scrollToItem(focusedIndex);
    }
  }, [flatChildren, props.focusedOption, props.selectProps]);

  const { focusedOption } = props;
  const focusedOptionRef = useRef(focusedOption);

  useEffect(() => {
    ignoreScrollToFocus.current = false;
    focusedOptionRef.current = focusedOption;
  }, [focusedOption]);

  return (
    <chakraComponents.MenuList {...props} innerRef={null as any}>
      {isNoOptions ? (
        children
      ) : (
        <List
          ref={listRef}
          height={listHeightPx > maxHeight ? maxHeight : listHeightPx}
          itemCount={flatChildren.length}
          onItemsRendered={(props) => {
            if (flatChildren.length - 2 < props.visibleStopIndex) {
              ignoreScrollToFocus.current =
                focusedOptionRef.current === focusedOption;
              dispatch(setLoadMore(true));
            }
          }}
          itemSize={(index) => {
            if (flatChildren[index]?.type === 'header') {
              return GROUP_HEADER_HEIGHT_PIXELS;
            }
            return OPTION_HEIGHT_PIXELS;
          }}
          width="100%"
        >
          {({ index, style }) => (
            <Box style={style}>
              {flatChildren[index]?.type === 'header'
                ? flatChildren[index].component
                : flatChildren[index]}
            </Box>
          )}
        </List>
      )}
    </chakraComponents.MenuList>
  );
};

const MenuListDefault = <Option extends {}>({
  children,
  ...props
}: MenuListProps<Option, false, GroupBase<Option>>) => {
  return (
    <chakraComponents.MenuList {...props}>{children}</chakraComponents.MenuList>
  );
};

export const MenuList = <Option extends {}>(
  props: MenuListProps<Option, false, GroupBase<Option>>
) => {
  const {
    selectProps: { disableVirtualization },
  } = props;
  if (disableVirtualization) {
    return <MenuListDefault {...props} />;
  }
  return <MenuListVirtualized {...props} />;
};
